import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Modal from '@/components/ui/modal';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import '@/components/ui/styles/CSAWFormScrollbar.css';
import { Textarea } from '@/components/ui/textarea';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
// import '@/components/ui/styles/customScrollBar.css'
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { AlertCircle, Loader2 } from "lucide-react";
import FormStepIndicator from '../FormStepIndicator';
import { CheckboxItem, CustomDatePicker, formatLabel, formatReviewValue, UploadCard } from './CSAWFormUtils';

const CREATE_CSAW_PERMIT = gql`
   mutation CreateCSAWPermit($input: CSAWPermitInput!) {
      createCSAWPermit(input: $input) {
         id
         applicationNumber
         status
         dateOfSubmission
         purpose
         files {
            officialReceipt { filename contentType }
            deedOfSale { filename contentType }
            specialPowerOfAttorney { filename contentType }
            forestTenureAgreement { filename contentType }
            businessPermit { filename contentType }
            certificateOfRegistration { filename contentType }
            woodProcessingPlantPermit { filename contentType }
         }
      }
   }
`;

const SAVE_CSAW_PERMIT_DRAFT = gql`
   mutation SaveCSAWPermitDraft($input: CSAWPermitInput!) {
      saveCSAWPermitDraft(input: $input) {
         id
         applicationNumber
         status
         purpose
         files {
            officialReceipt { filename contentType }
            deedOfSale { filename contentType }
            specialPowerOfAttorney { filename contentType }
            forestTenureAgreement { filename contentType }
            businessPermit { filename contentType }
            certificateOfRegistration { filename contentType }
            woodProcessingPlantPermit { filename contentType }
         }
      }
   }
`;

const GET_PREVIOUS_CERTIFICATE = gql`
   query GetPreviousCertificate($certificateNumber: String!) {
      getPreviousCertificate(certificateNumber: $certificateNumber) {
         id
         certificateNumber
         csawCertificateData {
            registrationType
            ownerName
            address
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
         }
      }
   }
`;

const CHECK_SERIAL_NUMBER = gql`
   query CheckSerialNumber($serialNumber: String!) {
      checkSerialNumber(serialNumber: $serialNumber)
   }
`;

const RenewalConfirmationDialog = ({ isOpen, onClose, previousCertData, onProceed }) => (
   <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-2xl">
         <AlertDialogHeader>
            <AlertDialogTitle>Certificate Renewal Details</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">
               Verification and renewal details for certificate {previousCertData?.certificateNumber}
            </AlertDialogDescription>

            <div className="space-y-4">
               <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800 font-medium">
                     Certificate {previousCertData?.certificateNumber} has been verified and is eligible for renewal.
                  </p>
               </div>

               <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                     <h4 className="font-medium text-foreground">Previous Certificate Details</h4>
                     <div className="bg-background border rounded-lg p-3">
                        <div className="space-y-2">
                           <div>
                              <span className="text-sm text-muted-foreground">Brand:</span>
                              <p className="font-medium">{previousCertData?.csawCertificateData.chainsawDetails.brand}</p>
                           </div>
                           <div>
                              <span className="text-sm text-muted-foreground">Model:</span>
                              <p className="font-medium">{previousCertData?.csawCertificateData.chainsawDetails.model}</p>
                           </div>
                           <div>
                              <span className="text-sm text-muted-foreground">Serial Number:</span>
                              <p className="font-medium">{previousCertData?.csawCertificateData.chainsawDetails.serialNumber}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="space-y-2">
                     <h4 className="font-medium text-foreground">Renewal Information</h4>
                     <div className="bg-background border rounded-lg p-3">
                        <div className="space-y-2">
                           <div>
                              <span className="text-sm text-muted-foreground">New Expiry Date:</span>
                              <p className="font-medium">
                                 {new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                 })}
                              </p>
                           </div>
                           <div>
                              <span className="text-sm text-muted-foreground">Validity:</span>
                              <p className="font-medium">2 Years</p>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="mt-4 text-sm text-muted-foreground">
                  Click proceed to continue with the renewal application using these details.
               </div>
            </div>
         </AlertDialogHeader>
         <AlertDialogFooter>
            <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
            <AlertDialogAction
               onClick={onProceed}
               className="bg-green-600 hover:bg-green-700 text-white"
            >
               Proceed with Renewal
            </AlertDialogAction>
         </AlertDialogFooter>
      </AlertDialogContent>
   </AlertDialog>
);

const ChainsawRegistrationForm = () => {
   const navigate = useNavigate();
   const [currentStep, setCurrentStep] = useState(() => {
      const savedStep = localStorage.getItem('csawFormStep');
      return savedStep ? parseInt(savedStep, 10) : 0;
   });
   const [formData, setFormData] = useState(() => {
      const savedFormData = localStorage.getItem('csawFormData');
      return savedFormData ? JSON.parse(savedFormData) : {
         applicationType: 'Chainsaw Registration',
         registrationType: '',
         chainsawStore: '',
         ownerName: '',
         address: '',
         phone: '',
         purpose: '',
         brand: '',
         model: '',
         serialNumber: '',
         dateOfAcquisition: new Date().toISOString(),
         powerOutput: '',
         maxLengthGuidebar: '',
         countryOfOrigin: '',
         purchasePrice: '',
         isOwner: false,
         isTenureHolder: false,
         isBusinessOwner: false,
         isPTPRHolder: false,
         isWPPHolder: false,
         isRenewal: false,
         previousPermitNumber: '',
         renewalDate: null,
         renewalExpiryDate: null,
         files: {
            officialReceipt: [],
            deedOfSale: [],
            specialPowerOfAttorney: [],
            forestTenureAgreement: [],
            businessPermit: [],
            certificateOfRegistration: [],
            woodProcessingPlantPermit: []
         },
         status: 'Draft',
         dateOfSubmission: new Date().toISOString(),
      };
   });
   const [modalOpen, setModalOpen] = useState(false);
   const [modalContent, setModalContent] = useState({ title: '', message: '' });
   const [customStore, setCustomStore] = useState('');
   const [createCSAWPermit] = useMutation(CREATE_CSAW_PERMIT);
   const [saveCSAWPermitDraft] = useMutation(SAVE_CSAW_PERMIT_DRAFT);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isSavingDraft, setIsSavingDraft] = useState(false);
   const [previousCertNumber, setPreviousCertNumber] = useState('');
   const [isVerifyingCert, setIsVerifyingCert] = useState(false);
   const [getPreviousCertificate] = useLazyQuery(GET_PREVIOUS_CERTIFICATE, {
      onCompleted: (data) => {
         if (data?.getPreviousCertificate) {
            setRenewalData(data.getPreviousCertificate);
            setShowRenewalDialog(true);
         }
         setIsVerifyingCert(false);
      },
      onError: (error) => {
         toast.error(error.message || 'Failed to verify certificate');
         setIsVerifyingCert(false);
         setPreviousCertNumber('');
      }
   });
   const [showSubmitDialog, setShowSubmitDialog] = useState(false);
   const [showDraftDialog, setShowDraftDialog] = useState(false);
   const [checkSerialNumber] = useLazyQuery(CHECK_SERIAL_NUMBER);
   const [showRenewalDialog, setShowRenewalDialog] = useState(false);
   const [renewalData, setRenewalData] = useState(null);
   const [validationErrors, setValidationErrors] = useState({});
   const [isVerifyingSerial, setIsVerifyingSerial] = useState(false);
   const firstInvalidFieldRef = useRef(null);

   const handleInputChange = async (e) => {
      const { name, value, type } = e.target;
      let processedValue = value;

      // Handle different input types
      if (type === 'number') {
         processedValue = value === '' ? '' : Number(value);
      } else if (type === 'checkbox') {
         processedValue = e.target.checked;
      } else if (type === 'date') {
         processedValue = value ? new Date(value).toISOString() : null;
      }

      setFormData(prev => ({
         ...prev,
         [name]: processedValue
      }));

      // Clear validation error
      if (validationErrors[name]) {
         setValidationErrors(prev => ({
            ...prev,
            [name]: undefined
         }));
      }
   };

   const handleSelectChange = (name, value) => {
      setFormData(prev => ({ ...prev, [name]: value }));
   };

   const handleFileChange = (e, documentType) => {
      if (e.target.files) {
         const newFiles = Array.from(e.target.files);
         setFormData(prev => ({
            ...prev,
            files: {
               ...prev.files,
               [documentType]: [...(prev.files[documentType] || []), ...newFiles]
            }
         }));
      }
   };

   const removeFile = (documentType, fileToRemove) => {
      setFormData(prev => ({
         ...prev,
         files: {
            ...prev.files,
            [documentType]: prev.files[documentType].filter(file => file !== fileToRemove)
         }
      }));
   };

   const validateStep = (stepNumber) => {
      // Check if validation is disabled in DevSettings
      const isValidationEnabled = localStorage.getItem('csawValidationEnabled') !== 'false';
      if (!isValidationEnabled) {
         return {}; // Return empty errors object if validation is disabled
      }

      const errors = {};

      if (stepNumber === 0) {
         if (!formData.registrationType) {
            errors.registrationType = 'Please select a registration type';
         }
         if (formData.registrationType === 'Renewal' &&
            !formData.previousCertificateNumber &&
            !renewalData) {
            errors.previousCertificateNumber = 'Please verify your previous certificate first';
         }
      }

      if (stepNumber === 1) {
         if (!formData.chainsawStore?.trim()) {
            errors.chainsawStore = 'Chainsaw store name is required';
         }
      }

      if (stepNumber === 4) {
         const requiredFields = {
            ownerName: 'Owner name is required',
            address: 'Address is required',
            phone: 'Phone number is required',
            purpose: 'Purpose is required',
            brand: 'Brand is required',
            model: 'Model is required',
            serialNumber: 'Serial number is required',
            dateOfAcquisition: 'Date of acquisition is required',
            powerOutput: 'Power output is required',
            maxLengthGuidebar: 'Maximum length of guidebar is required',
            countryOfOrigin: 'Country of origin is required',
            purchasePrice: 'Purchase price is required'
         };

         Object.entries(requiredFields).forEach(([field, message]) => {
            if (!formData[field]?.toString().trim()) {
               errors[field] = message;
            }
         });
      }

      return errors;
   };

   const handleNextStep = async () => {
      // Ensure we don't exceed the maximum number of steps
      if (currentStep >= steps.length - 1) {
         return;
      }

      const errors = validateStep(currentStep);
      setValidationErrors(errors);

      if (Object.keys(errors).length > 0) {
         // Use setTimeout to ensure DOM is updated
         setTimeout(() => {
            const firstErrorField = document.querySelector('[data-error="true"]');
            if (firstErrorField) {
               firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
               firstErrorField.focus();
            }
         }, 100);
         return;
      }

      // Check serial number when moving from Application Details step
      if (currentStep === 4) {
         try {
            setIsVerifyingSerial(true);
            const { data } = await checkSerialNumber({
               variables: { serialNumber: formData.serialNumber }
            });

            if (data?.checkSerialNumber) {
               setValidationErrors(prev => ({
                  ...prev,
                  serialNumber: 'This serial number is already registered'
               }));
               // Scroll to the serial number field
               setTimeout(() => {
                  const serialNumberField = document.querySelector('[name="serialNumber"]');
                  if (serialNumberField) {
                     serialNumberField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                     serialNumberField.focus();
                  }
               }, 100);
               return;
            }
         } catch (error) {
            console.error('Error checking serial number:', error);
            toast.error("Error verifying serial number. Please try again.");
            return;
         } finally {
            setIsVerifyingSerial(false);
         }
      }

      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
   };

   const handlePrevStep = () => {
      // Ensure we don't go below 0
      if (currentStep <= 0) {
         return;
      }
      setCurrentStep(prev => Math.max(prev - 1, 0));
   };

   const handleSaveAsDraft = () => {
      setShowDraftDialog(true);
   };

   const handleConfirmSaveAsDraft = async () => {
      try {
         setIsSavingDraft(true);
         const input = {
            registrationType: formData.registrationType,
            chainsawStore: formData.chainsawStore,
            ownerName: formData.ownerName,
            address: formData.address,
            phone: formData.phone,
            purpose: formData.purpose,
            brand: formData.brand,
            model: formData.model,
            serialNumber: formData.serialNumber,
            dateOfAcquisition: new Date(formData.dateOfAcquisition).toISOString(),
            powerOutput: formData.powerOutput.toString(),
            maxLengthGuidebar: formData.maxLengthGuidebar.toString(),
            countryOfOrigin: formData.countryOfOrigin,
            purchasePrice: parseFloat(formData.purchasePrice),
            isOwner: Boolean(formData.isOwner),
            isTenureHolder: Boolean(formData.isTenureHolder),
            isBusinessOwner: Boolean(formData.isBusinessOwner),
            isPTPRHolder: Boolean(formData.isPTPRHolder),
            isWPPHolder: Boolean(formData.isWPPHolder),
            files: {}
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  const content = await readFileAsBase64(file);
                  return {
                     filename: file.name,
                     contentType: file.type,
                     data: content
                  };
               }));
            }
         }

         const token = localStorage.getItem('token');
         const { data } = await saveCSAWPermitDraft({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.saveCSAWPermitDraft) {
            setModalContent({
               title: 'Draft saved successfully!',
               message: 'Do you want to view your applications?'
            });
            setModalOpen(true);
            localStorage.removeItem('csawFormStep');
            localStorage.removeItem('csawFormData');
         }
      } catch (error) {
         console.error('Error saving draft:', error);
         toast.error("Error saving draft: " + error.message);
      } finally {
         setIsSavingDraft(false);
         setShowDraftDialog(false);
      }
   };

   const handleSubmit = async (e) => {
      if (e) e.preventDefault();
      setShowSubmitDialog(true);
   };

   const handleConfirmSubmit = async () => {
      try {
         setIsSubmitting(true);
         const input = {
            registrationType: formData.registrationType,
            chainsawStore: formData.chainsawStore,
            ownerName: formData.ownerName,
            address: formData.address,
            phone: formData.phone,
            purpose: formData.purpose,
            brand: formData.brand,
            model: formData.model,
            serialNumber: formData.serialNumber,
            dateOfAcquisition: new Date(formData.dateOfAcquisition).toISOString(),
            powerOutput: formData.powerOutput.toString(),
            maxLengthGuidebar: formData.maxLengthGuidebar.toString(),
            countryOfOrigin: formData.countryOfOrigin,
            purchasePrice: parseFloat(formData.purchasePrice),
            isOwner: Boolean(formData.isOwner),
            isTenureHolder: Boolean(formData.isTenureHolder),
            isBusinessOwner: Boolean(formData.isBusinessOwner),
            isPTPRHolder: Boolean(formData.isPTPRHolder),
            isWPPHolder: Boolean(formData.isWPPHolder),
            files: {}
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  const content = await readFileAsBase64(file);
                  return {
                     filename: file.name,
                     contentType: file.type,
                     data: content
                  };
               }));
            }
         }

         const token = localStorage.getItem('token');
         const { data } = await createCSAWPermit({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.createCSAWPermit) {
            setModalContent({
               title: 'Application submitted successfully!',
               message: 'Do you want to view your application?'
            });
            setModalOpen(true);
            localStorage.removeItem('csawFormStep');
            localStorage.removeItem('csawFormData');
         }
      } catch (error) {
         console.error('Error submitting application:', error);
         toast.error("Error submitting application: " + error.message);
      } finally {
         setIsSubmitting(false);
         setShowSubmitDialog(false);
      }
   };

   // Helper function to read file as base64
   const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result.split(',')[1]);
         reader.onerror = error => reject(error);
         reader.readAsDataURL(file);
      });
   };

   const handleCheckboxChange = (e) => {
      const { name, checked } = e.target;

      // Map of checkbox names to their corresponding file types
      const fileTypeMap = {
         isOwner: 'specialPowerOfAttorney',
         isTenureHolder: 'forestTenureAgreement',
         isBusinessOwner: 'businessPermit',
         isPTPRHolder: 'certificateOfRegistration',
         isWPPHolder: 'woodProcessingPlantPermit'
      };

      // Clear associated files if requirement is unchecked
      if (!checked && fileTypeMap[name]) {
         setFormData(prev => ({
            ...prev,
            [name]: checked,
            files: {
               ...prev.files,
               [fileTypeMap[name]]: [] // Clear the files for this requirement
            }
         }));
      } else {
         setFormData(prev => ({ ...prev, [name]: checked }));
      }
   };

   const handleDateChange = (date) => {
      setFormData(prev => ({ ...prev, dateOfAcquisition: date }));
   };

   const verifyPreviousCertificate = async () => {
      if (!previousCertNumber.trim()) {
         toast.error('Please enter a certificate number');
         return;
      }

      try {
         setIsVerifyingCert(true);
         const { data } = await getPreviousCertificate({
            variables: { certificateNumber: previousCertNumber.trim() }
         });

         if (data?.getPreviousCertificate) {
            setRenewalData(data.getPreviousCertificate);
            setShowRenewalDialog(true);
            // Pre-fill form data with previous certificate details
            setFormData(prev => ({
               ...prev,
               ownerName: data.getPreviousCertificate.csawCertificateData.ownerName,
               address: data.getPreviousCertificate.csawCertificateData.address,
               chainsawDetails: data.getPreviousCertificate.csawCertificateData.chainsawDetails
            }));
         }
      } catch (error) {
         console.error('Certificate verification error:', error);
         toast.error(error.message || 'Failed to verify certificate');
      } finally {
         setIsVerifyingCert(false);
      }
   };

   const handleProceedRenewal = () => {
      try {
         if (!renewalData) {
            toast.error("Please verify your certificate first");
            return;
         }

         // Show success message
         toast.success("Certificate renewed successfully!", {
            description: "Your certificate has been renewed. You will be redirected to your applications.",
            duration: 3000
         });

         // Close the renewal dialog
         setShowRenewalDialog(false);

         // Short delay before redirect to ensure toast is visible
         setTimeout(() => {
            navigate('/applicationsStatus');
         }, 2000);

      } catch (error) {
         console.error('Error handling renewal:', error);
         toast.error("Failed to complete renewal process");
      }
   };

   const steps = [
      { title: "Registration Type", description: "Choose registration type" },
      { title: "Accredited Chainsaw Store", description: "Select chainsaw store" },
      { title: "Document Requirements", description: "Specify document requirements" },
      { title: "Upload Documents", description: "Upload necessary documents" },
      { title: "Application Details", description: "Fill in application details" },
      { title: "Review  Your Application", description: "Review your application" },
   ];

   const uploadCardsCount = Object.values(formData).filter(value => value === true).length;
   const isScrollable = uploadCardsCount > 3;

   useEffect(() => {
      localStorage.setItem('csawFormStep', currentStep.toString());
   }, [currentStep]);

   useEffect(() => {
      localStorage.setItem('csawFormData', JSON.stringify(formData));
   }, [formData]);

   useEffect(() => {
      const handleBeforeUnload = (e) => {
         // If it's a refresh, persist the data
         if (e.persisted) {
            return;
         }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
         window.removeEventListener('beforeunload', handleBeforeUnload);

         // Get the navigation type
         const perfEntries = performance.getEntriesByType('navigation');
         const navType = perfEntries.length > 0 ? perfEntries[0].type : '';

         // Only clear if it's not a refresh/reload
         if (navType !== 'reload') {
            // Clear form data from localStorage when component unmounts (not on refresh)
            localStorage.removeItem('csawFormStep');
            localStorage.removeItem('csawFormData');
            // Reset form data to initial state
            setFormData({
               applicationType: 'Chainsaw Registration',
               registrationType: '',
               chainsawStore: '',
               ownerName: '',
               address: '',
               phone: '',
               purpose: '',
               brand: '',
               model: '',
               serialNumber: '',
               dateOfAcquisition: new Date().toISOString(),
               powerOutput: '',
               maxLengthGuidebar: '',
               countryOfOrigin: '',
               purchasePrice: '',
               isOwner: false,
               isTenureHolder: false,
               isBusinessOwner: false,
               isPTPRHolder: false,
               isWPPHolder: false,
               isRenewal: false,
               previousPermitNumber: '',
               renewalDate: null,
               renewalExpiryDate: null,
               files: {
                  officialReceipt: [],
                  deedOfSale: [],
                  specialPowerOfAttorney: [],
                  forestTenureAgreement: [],
                  businessPermit: [],
                  certificateOfRegistration: [],
                  woodProcessingPlantPermit: []
               },
               status: 'Draft',
               dateOfSubmission: new Date().toISOString(),
            });
            // Reset current step
            setCurrentStep(0);
         }
      };
   }, []);

   const renderStep = (stepNumber) => {
      switch (stepNumber) {
         case 0:
            return (
               <div className="space-y-4">
                  <div>
                     <RadioGroup
                        onValueChange={(value) => {
                           handleSelectChange('registrationType', value);
                           // Clear validation error
                           setValidationErrors(prev => ({
                              ...prev,
                              registrationType: undefined
                           }));
                        }}
                        value={formData.registrationType}
                        className="space-y-3"
                     >
                        <div
                           className={cn(
                              `w-full p-4 rounded-md transition-colors cursor-pointer`,
                              validationErrors.registrationType && "border-2 border-red-500",
                              formData.registrationType === 'New'
                                 ? 'bg-green-100 hover:bg-green-50 dark:bg-green-900 dark:hover:bg-green-800'
                                 : 'bg-gray-100 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600'
                           )}
                           data-error={validationErrors.registrationType ? "true" : "false"}
                        >
                           <div className="flex items-center space-x-3">
                              <RadioGroupItem value="New" id="new" className="text-green-600 dark:text-green-400" />
                              <Label
                                 htmlFor="new"
                                 className={`text-base font-medium cursor-pointer
                                    ${formData.registrationType === 'New'
                                       ? 'text-green-700 dark:text-green-300'
                                       : 'text-gray-700 dark:text-gray-300'}`}
                              >
                                 New Registration
                              </Label>
                           </div>
                        </div>

                        <div
                           className={`w-full p-4 rounded-md transition-colors cursor-pointer
                              ${formData.registrationType === 'Renewal'
                                 ? 'bg-green-100 hover:bg-green-50 dark:bg-green-900 dark:hover:bg-green-800'
                                 : 'bg-gray-100 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600'}`}
                        >
                           <div className="flex items-center space-x-3">
                              <RadioGroupItem value="Renewal" id="renewal" className="text-green-600 dark:text-green-400" />
                              <Label
                                 htmlFor="renewal"
                                 className={`text-base font-medium cursor-pointer
                                    ${formData.registrationType === 'Renewal'
                                       ? 'text-green-700 dark:text-green-300'
                                       : 'text-gray-700 dark:text-gray-300'}`}
                              >
                                 Renewal
                              </Label>
                           </div>
                        </div>
                     </RadioGroup>
                     {validationErrors.registrationType && (
                        <div className="flex items-center mt-1 text-sm text-red-500">
                           <AlertCircle className="h-4 w-4 mr-1" />
                           {validationErrors.registrationType}
                        </div>
                     )}
                  </div>

                  {formData.registrationType === 'Renewal' && (
                     <div className="space-y-4">
                        <div className="flex gap-2">
                           <Input
                              type="text"
                              name="previousCertificateNumber"
                              value={previousCertNumber}
                              onChange={(e) => setPreviousCertNumber(e.target.value)}
                              placeholder="Enter previous certificate number"
                              disabled={isVerifyingCert || renewalData}
                           />
                           <Button
                              onClick={verifyPreviousCertificate}
                              disabled={isVerifyingCert || renewalData}
                              className="bg-green-600 hover:bg-green-700"
                           >
                              {isVerifyingCert ? (
                                 <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                 'Verify'
                              )}
                           </Button>
                        </div>
                        {validationErrors.previousCertificateNumber && (
                           <div className="text-sm text-red-500">
                              {validationErrors.previousCertificateNumber}
                           </div>
                        )}
                     </div>
                  )}
               </div>
            );
         case 1:
            return renderChainsawStoreStep();
         case 2:
            return renderCheckboxGroup();
         case 3:
            return renderUploadCards();
         case 4:
            return renderApplicationDetails();
         case 5:
            return (
               <div className="space-y-6 h-[550px] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                  <div className="review-step-container csaw-form-scrollbar flex-grow overflow-auto">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        {Object.entries(formData)
                           .filter(([key]) => [
                              'registrationType',
                              'chainsawStore',
                              'ownerName',
                              'address',
                              'phone',
                              'purpose',
                              'brand',
                              'model',
                              'serialNumber',
                              'dateOfAcquisition',
                              'powerOutput',
                              'maxLengthGuidebar',
                              'countryOfOrigin',
                              'purchasePrice',
                              'isOwner',
                              'isTenureHolder',
                              'isBusinessOwner',
                              'isPTPRHolder',
                              'isWPPHolder'
                           ].includes(key))
                           .map(([key, value]) => (
                              <div key={key} className="bg-background border rounded-lg p-3">
                                 <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">{formatLabel(key)}</h4>
                                 <p className="text-foreground text-sm break-words">
                                    {formatReviewValue(key, value)}
                                 </p>
                              </div>
                           ))}
                     </div>

                     <div className="bg-background border rounded-lg p-3 mt-4">
                        <h4 className="font-semibold text-green-600 dark:text-green-500 mb-2 text-sm">Uploaded Files</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                           {Object.entries(formData.files).map(([docType, files]) => (
                              <div key={docType} className="border-b dark:border-gray-700 pb-2">
                                 <h5 className="font-medium text-foreground mb-1 text-xs">{formatLabel(docType)}</h5>
                                 {files.length > 0 ? (
                                    <ul className="list-disc list-inside">
                                       {files.map((file, index) => (
                                          <li key={index} className="text-xs text-muted-foreground truncate">{file.name}</li>
                                       ))}
                                    </ul>
                                 ) : (
                                    <p className="text-xs text-muted-foreground">No files uploaded</p>
                                 )}
                              </div>
                           ))}
                        </div>
                     </div>
                  </div>
               </div>
            );
         default:
            return null;
      }
   };

   const renderChainsawStoreStep = () => (
      <div className="space-y-4 pt-2">
         <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 font-semibold">
            Please enter the name of the store where you purchased your chainsaw.
         </p>
         <div className="space-y-2">
            <Label htmlFor="chainsawStore" className="dark:text-white">Chainsaw Store Name</Label>
            <div
               className={cn(
                  validationErrors.chainsawStore &&
                  "border-2 border-red-500 rounded-md p-1"
               )}
               data-error={validationErrors.chainsawStore ? "true" : "false"}
            >
               <Input
                  id="chainsawStore"
                  name="chainsawStore"
                  value={formData.chainsawStore}
                  onChange={handleInputChange}
                  placeholder="Enter the name of the store"
                  required
                  className="dark:bg-gray-700 dark:text-white"
               />
               {validationErrors.chainsawStore && (
                  <div className="flex items-center mt-1 text-sm text-red-500">
                     <AlertCircle className="h-4 w-4 mr-1" />
                     {validationErrors.chainsawStore}
                  </div>
               )}
            </div>
         </div>
      </div>
   );

   const renderCheckboxGroup = () => (
      <div className="space-y-4">
         <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-semibold">
            Please check the boxes that apply to you. In the next step, you will be required to upload the corresponding documents.
         </p>
         <div className="space-y-2">
            <CheckboxItem
               id="isOwner"
               label="Are you the owner of the Chainsaw?"
               checked={formData.isOwner}
               onChange={handleCheckboxChange}
            />
            <CheckboxItem
               id="isTenureHolder"
               label="Are you a Tenure holder?"
               checked={formData.isTenureHolder}
               onChange={handleCheckboxChange}
            />
            <CheckboxItem
               id="isBusinessOwner"
               label="Are you a business owner?"
               checked={formData.isBusinessOwner}
               onChange={handleCheckboxChange}
            />
            <CheckboxItem
               id="isPTPRHolder"
               label="Are you a Private Land Tree Plantation Registration (PTPR) holder?"
               checked={formData.isPTPRHolder}
               onChange={handleCheckboxChange}
            />
            <CheckboxItem
               id="isWPPHolder"
               label="Are you a Licensed Wood Processor/Wood Processing Plant (WPP) holder?"
               checked={formData.isWPPHolder}
               onChange={handleCheckboxChange}
            />
         </div>
      </div>
   );

   const renderUploadCards = () => (
      <div className="step-3-container">
         <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-semibold">
            Please upload the required documents.
         </p>
         <div className="upload-cards-container space-y-3 h-[calc(100vh-28rem)] overflow-y-auto pr-2 mt-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
            {/* Official Receipt - Always required */}
            <div
               className={cn(
                  validationErrors.officialReceipt && "border-2 border-red-500 rounded-md p-1"
               )}
               data-error={validationErrors.officialReceipt ? "true" : "false"}
            >
               <UploadCard
                  label="Official Receipt of Chainsaw Purchase"
                  documentLabel="Upload 1 certified copy and 1 original for verification (or Affidavit of Ownership if original is lost)"
                  files={formData.files.officialReceipt}
                  onFileChange={(e) => {
                     handleFileChange(e, 'officialReceipt');
                     if (validationErrors.officialReceipt) {
                        setValidationErrors(prev => ({ ...prev, officialReceipt: undefined }));
                     }
                  }}
                  onRemoveFile={(file) => removeFile('officialReceipt', file)}
               />
               {validationErrors.officialReceipt && (
                  <div className="flex items-center mt-1 text-sm text-red-500">
                     <AlertCircle className="h-4 w-4 mr-1" />
                     {validationErrors.officialReceipt}
                  </div>
               )}
            </div>

            {/* Deed of Sale - Optional */}
            <UploadCard
               label="Notarized Deed of Absolute Sale"
               documentLabel="Upload 1 original (required if transfer of ownership)"
               files={formData.files.deedOfSale}
               onFileChange={(e) => handleFileChange(e, 'deedOfSale')}
               onRemoveFile={(file) => removeFile('deedOfSale', file)}
            />

            {/* Special Power of Attorney - Required if not owner */}
            {!formData.isOwner && (
               <div
                  className={cn(
                     validationErrors.specialPowerOfAttorney && "border-2 border-red-500 rounded-md p-1"
                  )}
                  data-error={validationErrors.specialPowerOfAttorney ? "true" : "false"}
               >
                  <UploadCard
                     label="Special Power of Attorney"
                     documentLabel="Upload document if you are not the owner of the Chainsaw"
                     files={formData.files.specialPowerOfAttorney}
                     onFileChange={(e) => {
                        handleFileChange(e, 'specialPowerOfAttorney');
                        if (validationErrors.specialPowerOfAttorney) {
                           setValidationErrors(prev => ({ ...prev, specialPowerOfAttorney: undefined }));
                        }
                     }}
                     onRemoveFile={(file) => removeFile('specialPowerOfAttorney', file)}
                  />
                  {validationErrors.specialPowerOfAttorney && (
                     <div className="flex items-center mt-1 text-sm text-red-500">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {validationErrors.specialPowerOfAttorney}
                     </div>
                  )}
               </div>
            )}

            {/* Forest Tenure Agreement - Required if tenure holder */}
            {formData.isTenureHolder && (
               <div
                  className={cn(
                     validationErrors.forestTenureAgreement && "border-2 border-red-500 rounded-md p-1"
                  )}
                  data-error={validationErrors.forestTenureAgreement ? "true" : "false"}
               >
                  <UploadCard
                     label="Forest Tenure Agreement"
                     documentLabel="Upload certified True copy of forest Tenure Agreement"
                     files={formData.files.forestTenureAgreement}
                     onFileChange={(e) => {
                        handleFileChange(e, 'forestTenureAgreement');
                        if (validationErrors.forestTenureAgreement) {
                           setValidationErrors(prev => ({ ...prev, forestTenureAgreement: undefined }));
                        }
                     }}
                     onRemoveFile={(file) => removeFile('forestTenureAgreement', file)}
                  />
                  {validationErrors.forestTenureAgreement && (
                     <div className="flex items-center mt-1 text-sm text-red-500">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {validationErrors.forestTenureAgreement}
                     </div>
                  )}
               </div>
            )}

            {/* Business Permit - Required if business owner */}
            {formData.isBusinessOwner && (
               <div
                  className={cn(
                     validationErrors.businessPermit && "border-2 border-red-500 rounded-md p-1"
                  )}
                  data-error={validationErrors.businessPermit ? "true" : "false"}
               >
                  <UploadCard
                     label="Business Permit"
                     documentLabel="Upload Business Permit"
                     files={formData.files.businessPermit}
                     onFileChange={(e) => {
                        handleFileChange(e, 'businessPermit');
                        if (validationErrors.businessPermit) {
                           setValidationErrors(prev => ({ ...prev, businessPermit: undefined }));
                        }
                     }}
                     onRemoveFile={(file) => removeFile('businessPermit', file)}
                  />
                  {validationErrors.businessPermit && (
                     <div className="flex items-center mt-1 text-sm text-red-500">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {validationErrors.businessPermit}
                     </div>
                  )}
               </div>
            )}

            {/* Certificate of Registration - Required if PTPR holder */}
            {formData.isPTPRHolder && (
               <div
                  className={cn(
                     validationErrors.certificateOfRegistration && "border-2 border-red-500 rounded-md p-1"
                  )}
                  data-error={validationErrors.certificateOfRegistration ? "true" : "false"}
               >
                  <UploadCard
                     label="Certificate of Registration"
                     documentLabel="Upload Certificate of Registration for Private Land Tree Plantation Registration (PTPR)"
                     files={formData.files.certificateOfRegistration}
                     onFileChange={(e) => {
                        handleFileChange(e, 'certificateOfRegistration');
                        if (validationErrors.certificateOfRegistration) {
                           setValidationErrors(prev => ({ ...prev, certificateOfRegistration: undefined }));
                        }
                     }}
                     onRemoveFile={(file) => removeFile('certificateOfRegistration', file)}
                  />
                  {validationErrors.certificateOfRegistration && (
                     <div className="flex items-center mt-1 text-sm text-red-500">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {validationErrors.certificateOfRegistration}
                     </div>
                  )}
               </div>
            )}

            {/* Wood Processing Plant Permit - Required if WPP holder */}
            {formData.isWPPHolder && (
               <div
                  className={cn(
                     validationErrors.woodProcessingPlantPermit && "border-2 border-red-500 rounded-md p-1"
                  )}
                  data-error={validationErrors.woodProcessingPlantPermit ? "true" : "false"}
               >
                  <UploadCard
                     label="Wood Processing Plant Permit"
                     documentLabel="Upload Wood Processing Plant Permit"
                     files={formData.files.woodProcessingPlantPermit}
                     onFileChange={(e) => {
                        handleFileChange(e, 'woodProcessingPlantPermit');
                        if (validationErrors.woodProcessingPlantPermit) {
                           setValidationErrors(prev => ({ ...prev, woodProcessingPlantPermit: undefined }));
                        }
                     }}
                     onRemoveFile={(file) => removeFile('woodProcessingPlantPermit', file)}
                  />
                  {validationErrors.woodProcessingPlantPermit && (
                     <div className="flex items-center mt-1 text-sm text-red-500">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {validationErrors.woodProcessingPlantPermit}
                     </div>
                  )}
               </div>
            )}
         </div>
      </div>
   );

   const renderApplicationDetailsInput = (id, label, placeholder = "", type = "text") => (
      <div className="space-y-2">
         <Label htmlFor={id} className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
         </Label>
         <div
            className={cn(
               validationErrors[id]
                  ? "border-2 border-red-500 rounded-lg p-1"
                  : "focus-within:ring-2 focus-within:ring-green-500/20 rounded-lg transition-all duration-200"
            )}
            data-error={validationErrors[id] ? "true" : "false"}
         >
            <Input
               id={id}
               name={id}
               type={type}
               value={formData[id]?.toString() || ''}
               onChange={handleInputChange}
               placeholder={placeholder}
               required
               className="border-2 rounded-md bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm focus:ring-0 focus:border-green-500 transition-colors duration-200"
            />
            {validationErrors[id] && (
               <div className="flex items-center mt-1.5 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4 mr-1.5" />
                  {validationErrors[id]}
               </div>
            )}
         </div>
      </div>
   );

   const renderApplicationDetails = () => (
      <div className="space-y-6">
         <div className="space-y-6 h-[calc(100vh-24rem)] sm:h-[500px] overflow-y-auto pr-2 sm:pr-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-200 dark:[&::-webkit-scrollbar-thumb]:bg-gray-700 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 dark:[&::-webkit-scrollbar-track]:bg-gray-800/50">
            <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
               <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                  Owner Details
               </h3>
               <div className="space-y-4">
                  {renderApplicationDetailsInput('ownerName', 'Name', 'Full Name')}
                  {renderApplicationDetailsInput('address', 'Address', 'Brgy/Street, Municipality, Province')}
                  {renderApplicationDetailsInput('phone', 'Phone Number', 'e.g. 09123456789')}

                  <div className="space-y-2">
                     <Label htmlFor="purpose" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Purpose
                     </Label>
                     <div
                        className={cn(
                           validationErrors.purpose
                              ? "border-2 border-red-500 rounded-lg p-1"
                              : "focus-within:ring-2 focus-within:ring-green-500/20 rounded-lg transition-all duration-200"
                        )}
                        data-error={validationErrors.purpose ? "true" : "false"}
                     >
                        <Textarea
                           id="purpose"
                           name="purpose"
                           value={formData.purpose}
                           onChange={handleInputChange}
                           placeholder="Describe the purpose for chainsaw registration"
                           required
                           className="min-h-[80px] sm:min-h-[100px] border-2 rounded-md bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm focus:ring-0 focus:border-green-500 transition-colors duration-200"
                        />
                        {validationErrors.purpose && (
                           <div className="flex items-center mt-1.5 text-sm text-red-500">
                              <AlertCircle className="h-4 w-4 mr-1.5" />
                              {validationErrors.purpose}
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </div>

            <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
               <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                  Chainsaw Details
               </h3>
               <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     {renderApplicationDetailsInput('brand', 'Brand', 'Enter Brand')}
                     {renderApplicationDetailsInput('model', 'Model', 'Enter Model')}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     {renderApplicationDetailsInput('serialNumber', 'Serial No.', 'Enter Serial Number')}
                     <div className="space-y-2">
                        <Label htmlFor="dateOfAcquisition" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                           Date of Acquisition
                        </Label>
                        <div
                           className={cn(
                              validationErrors.dateOfAcquisition
                                 ? "border-2 border-red-500 rounded-lg p-1"
                                 : "focus-within:ring-2 focus-within:ring-green-500/20 rounded-lg transition-all duration-200"
                           )}
                           data-error={validationErrors.dateOfAcquisition ? "true" : "false"}
                        >
                           <CustomDatePicker
                              selectedDate={formData.dateOfAcquisition || new Date().toISOString()}
                              onChange={(date) => {
                                 handleDateChange(date);
                                 if (validationErrors.dateOfAcquisition) {
                                    setValidationErrors(prev => ({ ...prev, dateOfAcquisition: undefined }));
                                 }
                              }}
                           />
                           {validationErrors.dateOfAcquisition && (
                              <div className="flex items-center mt-1.5 text-sm text-red-500">
                                 <AlertCircle className="h-4 w-4 mr-1.5" />
                                 {validationErrors.dateOfAcquisition}
                              </div>
                           )}
                        </div>
                     </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     {renderApplicationDetailsInput('powerOutput', 'Power Output (kW/horsepower)', 'e.g. 5 kW or 2.0 horsepower')}
                     {renderApplicationDetailsInput('maxLengthGuidebar', 'Maximum Length of Guidebar (Inches)', 'e.g. 20 inches')}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     {renderApplicationDetailsInput('countryOfOrigin', 'Country of Origin', 'Enter Country of Origin')}
                     {renderApplicationDetailsInput('purchasePrice', 'Purchase Price', 'Enter Purchase Price', 'number')}
                  </div>
               </div>
            </div>
         </div>
      </div>
   );

   return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col justify-between pt-[83px]">
         <div className="container mx-auto px-2 sm:px-4 pb-8">
            <div className="max-w-4xl mx-auto">
               {/* Header Section */}
               <div className="text-center mb-4 sm:mb-8">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-[700] bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent leading-tight">
                     Chainsaw Registration
                  </h1>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-[700] bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent mb-2">
                     Application
                  </h2>
                  <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">Complete all steps to submit your application</p>
               </div>

               {/* Step Indicator - Hidden on Mobile */}
               <div className="hidden sm:block mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-md p-2">
                  <FormStepIndicator currentStep={currentStep + 1} formType={formData.applicationType} />
               </div>

               {/* Mobile Step Progress */}
               <div className="sm:hidden mb-4">
                  <div className="flex items-center justify-between px-2">
                     <div className="text-sm text-gray-600 dark:text-gray-400">
                        Step {currentStep + 1} of {steps.length}
                     </div>
                     <div className="text-sm font-medium text-green-600 dark:text-green-400">
                        {steps[currentStep]?.title}
                     </div>
                  </div>
                  <div className="mt-2 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                     <div
                        className="h-full bg-gradient-to-r from-green-600 to-green-500 transition-all duration-300"
                        style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                     />
                  </div>
               </div>

               {/* Main Form Card */}
               <Card className="shadow-xl bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border-0 rounded-xl overflow-hidden">
                  <CardHeader className="border-b border-gray-100 dark:border-gray-700 p-4 sm:p-6">
                     <CardTitle className="flex items-center text-lg sm:text-xl text-green-700 dark:text-green-500">
                        {steps[currentStep]?.title || 'Application Form'}
                     </CardTitle>
                     <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {steps[currentStep]?.description}
                     </p>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6">
                     <form onSubmit={handleSubmit} className="space-y-6">
                        {renderStep(currentStep)}
                     </form>
                  </CardContent>
                  <CardFooter className="border-t border-gray-100 dark:border-gray-700 p-4 sm:p-6">
                     <div className="flex flex-col sm:flex-row justify-between items-center gap-3 w-full">
                        <div className="w-full sm:w-auto order-2 sm:order-1">
                           {currentStep > 0 && (
                              <Button
                                 type="button"
                                 variant="outline"
                                 onClick={handlePrevStep}
                                 className="w-full sm:w-auto px-4 sm:px-6 py-2 border-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                                 disabled={isSubmitting || isSavingDraft}
                              >
                                 Previous
                              </Button>
                           )}
                        </div>
                        <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-3 order-1 sm:order-2">
                           {currentStep < steps.length - 1 ? (
                              <Button
                                 type="button"
                                 onClick={handleNextStep}
                                 className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                                 disabled={isSubmitting || isSavingDraft || isVerifyingSerial}
                              >
                                 {isVerifyingSerial && currentStep === 4 ? (
                                    <>
                                       <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                       Verifying...
                                    </>
                                 ) : (
                                    'Next'
                                 )}
                              </Button>
                           ) : (
                              <>
                                 <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleSaveAsDraft}
                                    className="w-full sm:w-auto px-4 sm:px-6 py-2 border-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    disabled={isSubmitting || isSavingDraft}
                                 >
                                    {isSavingDraft ? (
                                       <>
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                          Saving...
                                       </>
                                    ) : (
                                       'Save as Draft'
                                    )}
                                 </Button>
                                 <Button
                                    type="submit"
                                    onClick={handleSubmit}
                                    className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                                    disabled={isSubmitting || isSavingDraft}
                                 >
                                    {isSubmitting ? (
                                       <>
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                          Submitting...
                                       </>
                                    ) : (
                                       'Submit'
                                    )}
                                 </Button>
                              </>
                           )}
                        </div>
                     </div>
                  </CardFooter>
               </Card>
            </div>
         </div>
         <Modal
            isOpen={modalOpen}
            title={modalContent.title}
            message={modalContent.message}
            onClose={() => setModalOpen(false)}
            onHome={() => navigate('/home')}
            onApplications={() => navigate('/applicationsStatus')}
         />
         <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Submit Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to submit this application? Please review all details carefully before proceeding.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSubmit}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 hover:bg-green-700 text-white"
                  >
                     {isSubmitting ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Submitting...
                        </>
                     ) : (
                        'Submit'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Save as Draft</AlertDialogTitle>
                  <AlertDialogDescription>
                     Do you want to save your current progress as a draft? You can continue editing it later.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSaveAsDraft}
                     className="bg-green-600 hover:bg-green-700 text-white"
                  >
                     {isSavingDraft ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Saving...
                        </>
                     ) : (
                        'Save Draft'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <RenewalConfirmationDialog
            isOpen={showRenewalDialog}
            onClose={() => setShowRenewalDialog(false)}
            previousCertData={renewalData}
            onProceed={handleProceedRenewal}
         />
      </div>
   );
};

export default ChainsawRegistrationForm;
